import { motion } from 'framer-motion';
import { Users, Share2, BarChart3, Smartphone, CheckCircle, ArrowRight } from 'lucide-react';
import { useInView } from '../../hooks/useAnimations';
import Button from '../UI/Button';

// Import feature images from avatarlogo folder
import heroimage from '../../assets/avatarlogo/system.png';
import leadsimagewithgirl from '../../assets/avatarlogo/leadsimagewithgirl.png';
import reporting from '../../assets/avatarlogo/reporting.png';
import system from '../../assets/avatarlogo/system.png';
import trendingupimage from '../../assets/avatarlogo/trendingupimage.png';
import appdashboard from '../../assets/appdashboard.jpeg';

// iPhone Mockup Component
const IPhoneMockup = ({ image, alt }) => (
  <div className="relative mx-auto" style={{ maxWidth: '280px' }}>
    {/* iPhone Frame */}
    <div className="relative bg-slate-900 rounded-[45px] p-3 shadow-2xl">
      {/* Notch */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-slate-900 rounded-b-2xl z-20" />

      {/* Dynamic Island */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-30" />

      {/* Screen Bezel */}
      <div className="relative bg-black rounded-[38px] overflow-hidden">
        {/* Screen Content */}
        <div className="relative aspect-[9/19.5] overflow-hidden">
          <img
            src={image}
            alt={alt}
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>

      {/* Side Buttons */}
      <div className="absolute -left-1 top-24 w-1 h-8 bg-slate-800 rounded-r" />
      <div className="absolute -left-1 top-36 w-1 h-12 bg-slate-800 rounded-r" />
      <div className="absolute -left-1 top-52 w-1 h-12 bg-slate-800 rounded-r" />
      <div className="absolute -right-1 top-32 w-1 h-16 bg-slate-800 rounded-l" />
    </div>

    {/* Reflection Overlay */}
    <div className="absolute inset-0 rounded-[45px] bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none" />
  </div>
);

const features = [
  {
    icon: Users,
    title: 'Lead Management System',
    description: 'Capture, organize, and nurture leads with intelligent scoring and automated workflows. Never miss a potential customer again.',
    bulletPoints: [
      'Lead creation with custom fields',
      'Excel/CSV bulk import',
      'Smart lead allocation based on performance',
      'Advanced search and filtering',
      'Complete lead history tracking'
    ],
    image: leadsimagewithgirl,
    imageAlt: 'Lead Management Dashboard',
  },
  {
    icon: Smartphone,
    title: 'Telecaller Mobile App',
    description: 'Empower your team with a powerful mobile application for managing leads on the go. Stay connected anywhere, anytime.',
    bulletPoints: [
      'Attendance check-in/check-out',
      'View and manage assigned leads',
      'Call directly from the app',
      'Update call status instantly',
      'Follow-up reminders and alerts'
    ],
    image: appdashboard,
    imageAlt: 'Mobile App Dashboard',
    usePhoneMockup: true,
  },
  {
    icon: BarChart3,
    title: 'Call Monitoring & Reports',
    description: 'Track every call, measure performance, and get actionable insights to improve your team\'s productivity and conversions.',
    bulletPoints: [
      'Outgoing call tracking',
      'Connected vs missed calls analysis',
      'Productivity reports by agent',
      'Performance analytics dashboard',
      'Conversion tracking and follow-ups'
    ],
    image: reporting,
    imageAlt: 'Reports Dashboard',
  },
  {
    icon: Share2,
    title: 'Admin Dashboard & Team Management',
    description: 'Complete control over your operations. Manage users, track attendance, and monitor team performance in real-time.',
    bulletPoints: [
      'User management and roles',
      'Attendance tracking system',
      'Daily performance reports',
      'Real-time operational monitoring',
      'Team performance analytics'
    ],
    image: system,
    imageAlt: 'Admin Dashboard',
  },
];

const FeatureCard = ({ feature, index, isInView }) => {
  const isEven = index % 2 === 0;
  const IconComponent = feature.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: 0.2 + index * 0.15, duration: 0.6 }}
      className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${index > 0 ? 'mt-24 lg:mt-32' : ''}`}
    >
      {/* Content Side */}
      <div className={`${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.3 + index * 0.1 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 mb-4"
        >
          <IconComponent className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-medium text-purple-700">Feature {index + 1}</span>
        </motion.div>

        {/* Title */}
        <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
          {feature.title}
        </h3>

        {/* Description */}
        <p className="text-lg text-slate-600 mb-6 leading-relaxed">
          {feature.description}
        </p>

        {/* Bullet Points */}
        <ul className="space-y-3 mb-8">
          {feature.bulletPoints.map((point, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.4 + i * 0.1 + index * 0.05 }}
              className="flex items-start gap-3"
            >
              <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span className="text-slate-700">{point}</span>
            </motion.li>
          ))}
        </ul>

        {/* CTA Link */}
        <motion.a
          href="#features"
          className="inline-flex items-center gap-2 text-purple-600 font-medium hover:text-purple-700 transition-colors group"
          whileHover={{ x: 5 }}
        >
          Learn more
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </motion.a>
      </div>

      {/* Image Side */}
      <div className={`${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95, x: isEven ? 30 : -30 }}
          animate={isInView ? { opacity: 1, scale: 1, x: 0 } : {}}
          transition={{ delay: 0.3 + index * 0.1, duration: 0.6 }}
          className="relative"
        >
          {/* Image Container */}
          <div className="relative">
            {feature.usePhoneMockup ? (
              <IPhoneMockup image={feature.image} alt={feature.imageAlt} />
            ) : (
              <img
                src={feature.image}
                alt={feature.imageAlt}
                className="w-full h-auto object-cover"
              />
            )}
          </div>

          {/* Floating Accent Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.6 + index * 0.1 }}
            className={`absolute ${isEven ? '-bottom-4 -left-4' : '-bottom-4 -right-4'} bg-white rounded-xl shadow-xl shadow-purple-500/10 border border-purple-100 p-3 w-44`}
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                <IconComponent className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-900">Active</div>
                <div className="text-xs text-slate-500">Real-time</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const Features = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <section id="features" ref={ref} className="py-24 relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ x: [0, 50, 0], y: [0, -30, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/4 right-0 w-[400px] h-[400px] rounded-full orb-purple"
        />
        <motion.div
          animate={{ x: [0, -40, 0], y: [0, 40, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
          className="absolute bottom-1/4 left-0 w-[500px] h-[500px] rounded-full orb-indigo"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 lg:mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 mb-6"
          >
            <span className="text-sm font-medium text-purple-700">Powerful Features</span>
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
            Everything You Need to{' '}
            <span className="text-gradient">Scale Your Revenue</span>
          </h2>

          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Powerful tools designed to help your team close more deals and deliver exceptional customer experiences. All in one unified platform.
          </p>
        </motion.div>

        {/* Features List */}
        <div className="space-y-8">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              feature={feature}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
          className="mt-20 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 rounded-2xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200">
            <div className="text-left">
              <h3 className="text-lg font-semibold text-slate-900">Ready to get started?</h3>
              <p className="text-slate-600">Start your 14-day free trial today.</p>
            </div>
            <Button
              variant="primary"
              className="shadow-lg shadow-purple-500/25"
            >
              Start Free Trial
            </Button>
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default Features;